/**
 * One person, everything this academy knows about them.
 *
 * Reached from the list at /admin/people/:username. It answers the questions a row
 * cannot: why this person is here at all, what they can do, which courses they teach on
 * and which they are taking.
 *
 * NO ENDPOINT OF ITS OWN. It reads the same members query as the list — same key, same
 * cache — so arriving from a row is instant and a cold deep link costs exactly one
 * request. A per-person endpoint would be a second definition of "who is in this
 * academy", and those two would eventually disagree.
 *
 * THE ACCESS CONTROL IS THE SAME CONTROL as the list's, deliberately: same picker, same
 * confirmation for promotion, same refusals from the server. A second way to change
 * someone's authority that behaves slightly differently is how a tool loses people's
 * trust.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  SelectField,
  Spinner,
  type Column,
  DataTable,
} from '../../shared/ui';
import { getConfig } from '../../shared/lib/config';
import {
  assign,
  assignmentHref,
  levelBlurb,
  levelOptions,
  listAssignments,
  unassign,
  type Assignment,
  type AssignmentLevel,
} from '../../shared/lib/assignments';
import { listCourses, listLibraries } from '../../shared/lib/studio';
import { ANONYMOUS, loadSession, personaOf } from '../../shared/lib/session';
import {
  PERSONA_BLURBS,
  PERSONA_ORDER,
  SOURCE_BLURBS,
  SOURCE_ORDER,
  canRemove,
  listMembers,
  memberError,
  personaLabel,
  removeMember,
  setMemberPersona,
  sourceLabel,
  type Member,
  type MemberEnrolment,
  type MemberPersona,
} from '../../shared/lib/team';
import { PageHeader } from '../AdminShell';
import { LearnerAnswers } from '../LearnerAnswers';
import { ActionError, RemoveDialog, joinedOn } from './AdminPeople';
import { LearningPathAccess } from './LearningPathAccess';
import '../routes/admin-team.css';
import './people.css';

const PERSONA_OPTIONS: [string, string][] = PERSONA_ORDER.map((persona) => [
  persona,
  personaLabel(persona),
]);

/** Where a course key goes in the admin app. Keys carry ':' and '+', so they encode. */
const courseHref = (courseId: string) => `/admin/courses/${encodeURIComponent(courseId)}`;

export default function PersonDetail() {
  const { username = '' } = useParams();
  const config = getConfig();
  const slug = config.tenant.slug;
  const queryClient = useQueryClient();

  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const viewer = personaOf(session.data ?? ANONYMOUS, config.tenant.orgs);
  const canManage = viewer === 'tenant_admin' || viewer === 'superadmin';
  const me = session.data?.user?.username ?? '';

  const members = useQuery({
    queryKey: ['tenant-members', slug],
    queryFn: () => listMembers(slug),
  });

  const [promoting, setPromoting] = useState<MemberPersona | null>(null);
  const [removing, setRemoving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Which of this learner's enrolments the answers drill-in is open on, if any. One at a
  // time: the panel names the course in its own title, and two open at once would need two
  // titles fighting for the same space.
  const [answersFor, setAnswersFor] = useState<MemberEnrolment | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['tenant-members', slug] });

  const change = useMutation({
    mutationFn: (next: MemberPersona) => setMemberPersona(slug, username, next),
    onSuccess: (member) => {
      setPromoting(null);
      setNotice(`${member.name} is now ${personaLabel(member.persona).toLowerCase()}.`);
      void refresh();
    },
  });

  const remove = useMutation({
    mutationFn: () => removeMember(slug, username),
    onSuccess: () => {
      setRemoving(false);
      setNotice('They no longer have access to this academy. Their account is untouched.');
      void refresh();
    },
  });

  const person = (members.data ?? []).find((member) => member.username === username);
  const failure = memberError(members.error);

  if (members.isLoading || session.isLoading) {
    return <Spinner label="Loading this person…" />;
  }

  if (members.isError) {
    return (
      <>
        <PageHeader title={username} />
        <Banner tone="danger">
          {failure.status === 403 ? (
            <>
              <strong>You do not have access to this academy&rsquo;s people.</strong> Ask an
              administrator here to give you access.
            </>
          ) : (
            <>
              <strong>Could not load this person.</strong>{' '}
              {failure.detail ?? 'The platform did not answer. Retry in a moment.'}
            </>
          )}
        </Banner>
        <BackLink />
      </>
    );
  }

  if (!person) {
    return (
      <>
        <PageHeader title={username} />
        {notice ? (
          /* The most likely way to reach this: they were just removed, and removal took
             away the last reason they appeared. Saying so beats "not found". */
          <Banner tone="success">{notice} They are no longer listed here.</Banner>
        ) : (
          <Banner tone="info">
            <strong>Nobody by that username is connected to this academy.</strong> They may
            have an account on the platform without having registered here, holding a role
            here, or being enrolled in any of this academy&rsquo;s courses.
          </Banner>
        )}
        <BackLink />
      </>
    );
  }

  /**
   * Whose access may be edited from here — the list's rule, unchanged.
   *
   * Not yourself: the server refuses it, because demoting the account you are signed in
   * as is how an academy loses its last administrator. Not a platform administrator:
   * their authority comes from the platform, so a change here would appear to work and
   * do nothing.
   */
  const editable = canManage && person.username !== me && person.persona !== 'superadmin';

  return (
    <>
      <PageHeader
        title={person.name}
        description={person.email || person.username}
        actions={
          editable &&
          canRemove(person) && (
            <Button
              variant="ghost"
              onClick={() => {
                setNotice(null);
                remove.reset();
                setRemoving(true);
              }}
            >
              Remove from academy
            </Button>
          )
        }
      />

      <BackLink />

      {notice && <Banner tone="success">{notice}</Banner>}
      {change.isError && !promoting && (
        <ActionError error={change.error} what="That change was not applied." />
      )}
      {remove.isError && !removing && (
        <ActionError error={remove.error} what="They were not removed." />
      )}

      <div className="admin__cols">
        <div className="people__stack">
          <Card title="Access level">
            <div className="people__access">
              <Badge tone={person.persona === 'tenant_admin' ? 'info' : 'neutral'}>
                {personaLabel(person.persona)}
              </Badge>
              {editable && (
                <label className="team__persona">
                  <span className="team__persona-label">Access level for {person.name}</span>
                  <select
                    className="admin__select"
                    value={person.persona}
                    aria-label={`Access level for ${person.name}`}
                    disabled={change.isPending}
                    onChange={(event) => {
                      const next = event.target.value as MemberPersona;
                      if (next === person.persona) return;
                      setNotice(null);
                      // Promotion hands over authority over the whole academy, so it is
                      // confirmed. Everything the server lets through is reversible.
                      if (next === 'tenant_admin') {
                        change.reset();
                        setPromoting(next);
                      } else {
                        change.mutate(next);
                      }
                    }}
                  >
                    {PERSONA_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <p className="team__blurb">{PERSONA_BLURBS[person.persona]}</p>
            {!canManage && (
              <p className="team__quiet">
                Only an administrator of this academy can change what someone can do.
              </p>
            )}
            {person.username === me && (
              <p className="team__quiet">
                This is you. Your own access has to be changed by another administrator —
                that is what stops an academy from ending up with nobody to run it.
              </p>
            )}
          </Card>

          <Card title="Teaching">
            {person.staffCourses.length > 0 ? (
              <>
                <p className="team__quiet">
                  Courses in this academy where they hold a place on the course team.
                  Academy-wide authority is the access level above, not this.
                </p>
                <ul className="people__courses">
                  {person.staffCourses.map((courseId) => (
                    <li key={courseId}>
                      <Link to={courseHref(courseId)}>{titleOf(courseId, person)}</Link>
                      <span className="mono">{courseId}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="team__quiet">
                {person.persona === 'learner'
                  ? 'They are not on any course team in this academy.'
                  : 'No individual courses — what they can do here comes from their access level.'}
              </p>
            )}
          </Card>

          <Card title="Enrolments">
            {person.enrolments.length > 0 ? (
              <DataTable
                columns={enrolmentColumns((course) => setAnswersFor(course))}
                rows={person.enrolments}
                rowKey={(course) => course.courseId}
                empty={null}
              />
            ) : (
              <p className="team__quiet">
                They are not taking any of this academy&rsquo;s courses.
              </p>
            )}
          </Card>

          {/*
           * Its own query, keyed on the course chosen above — a learner can hold dozens of
           * enrolments and this only ever asks about the one an admin picked, never all of
           * them at once. See `LearnerAnswers.tsx` for why the per-block endpoint inside it
           * is gated a second time, behind expanding one row.
           */}
          {answersFor && (
            <Card
              title={`Answers in ${answersFor.title}`}
              action={
                <Button variant="ghost" onClick={() => setAnswersFor(null)}>
                  Close
                </Button>
              }
            >
              <LearnerAnswers
                courseId={answersFor.courseId}
                courseName={answersFor.title}
                username={person.username}
              />
            </Card>
          )}

          {/*
           * Learning-path access, and the audited way to withdraw a free one.
           *
           * BELOW Enrolments and not merged into it, because the two are different facts and
           * only one of them is a list. Enrolments is a measured list of this person's course
           * enrolments in this academy. This card cannot list their PATH enrolments — no admin
           * read reports them — so it says so and asks instead. Merging them would put a
           * question mark over rows that are not in doubt.
           */}
          <LearningPathAccess person={person} canManage={canManage} />

          {/*
           * Its own query, keyed ['person-assignments', slug, username], so it loads
           * independently of the members list and a slow answer here cannot hold up the
           * rest of this page.
           */}
          <Assignments slug={slug} person={person} canManage={canManage} orgs={config.tenant.orgs} />
        </div>

        <div className="people__stack">
          <Card title="Why they appear here">
            {person.sources.length > 0 ? (
              <dl className="team__legend">
                {SOURCE_ORDER.filter((source) => person.sources.includes(source)).map((source) => (
                  <div key={source}>
                    <dt>{sourceLabel(source)}</dt>
                    <dd>{SOURCE_BLURBS[source]}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="team__quiet">
                No reason was given for this person by the platform.
              </p>
            )}
          </Card>

          <Card title="Account">
            <dl className="people__facts">
              <div>
                <dt>Username</dt>
                <dd className="mono">{person.username}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{person.email || '—'}</dd>
              </div>
              <div>
                <dt>Account</dt>
                <dd>
                  {person.isActive ? (
                    <Badge tone="success">Active</Badge>
                  ) : (
                    <Badge tone="warning">Not activated</Badge>
                  )}
                </dd>
              </div>
              <div>
                <dt>Joined</dt>
                <dd>{joinedOn(person.joined)}</dd>
              </div>
            </dl>
            <p className="team__quiet">
              The account belongs to the person, not to this academy. Removing them from
              here never deletes it.
            </p>
          </Card>
        </div>
      </div>

      {promoting && (
        <Dialog
          title="Make this person an administrator?"
          onClose={() => setPromoting(null)}
          footer={
            <>
              <Button
                variant="primary"
                busy={change.isPending}
                onClick={() => change.mutate('tenant_admin')}
              >
                Make administrator
              </Button>
              <Button variant="ghost" onClick={() => setPromoting(null)}>
                Cancel
              </Button>
            </>
          }
        >
          <p>
            <strong>{person.name}</strong> will be able to do everything you can: add and
            remove people, change what anyone here can do — including you — and edit this
            academy&rsquo;s settings.
          </p>
          <p className="team__quiet">
            They keep everything they can do today. You can change them back later, as long
            as somebody else is still an administrator.
          </p>
          {change.isError && <ActionError error={change.error} what="Nothing was changed." />}
        </Dialog>
      )}

      {removing && (
        <RemoveDialog
          member={person}
          busy={remove.isPending}
          error={remove.isError ? remove.error : null}
          onClose={() => setRemoving(false)}
          onConfirm={() => remove.mutate()}
        />
      )}
    </>
  );
}

/* ----------------------------------------------------------------- assignments ---- */

/**
 * What this person has been given, individually, inside this academy.
 *
 * THE POINT OF THE WHOLE CARD. Access here has otherwise been all-or-nothing: hold an
 * academy-wide role and you see every course and library, hold none and you see nothing.
 * This is the third case — somebody with no academy-wide role who has been handed specific
 * work and sees exactly that.
 *
 * So the two states this card can be in are genuinely different, and it says which:
 *
 *   no academy-wide role   assignments are the only thing they can see. Listed, added and
 *                          taken back here.
 *   academy-wide role      assignments change nothing, because they already see it all.
 *                          Said plainly, and the add control is NOT shown — a control that
 *                          cannot alter anything is worse than no control, because the
 *                          administrator uses it and then cannot tell what happened.
 */
function Assignments({
  slug,
  person,
  canManage,
  orgs,
}: {
  slug: string;
  person: Member;
  canManage: boolean;
  orgs: string[];
}) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [removingItem, setRemovingItem] = useState<Assignment | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Reading who has what is a statement about somebody else's access, so the server gates
  // it the same way it gates changing it. Asking as anyone else would only earn a 403.
  const readable = canManage && person.persona !== 'superadmin';

  const assignments = useQuery({
    queryKey: ['person-assignments', slug, person.username],
    queryFn: () => listAssignments(slug, person.username),
    enabled: readable,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['person-assignments', slug, person.username] });

  const give = useMutation({
    mutationFn: ({ id, level }: { id: string; level: AssignmentLevel }) =>
      assign(slug, person.username, id, level),
    onSuccess: (item) => {
      setAdding(false);
      setNotice(`${person.name} can now reach ${item.title}.`);
      void refresh();
    },
  });

  const take = useMutation({
    mutationFn: (item: Assignment) => unassign(slug, person.username, item.id),
    onSuccess: (_result, item) => {
      setRemovingItem(null);
      setNotice(`${person.name} no longer has access to ${item.title}. Nothing else changed.`);
      void refresh();
    },
  });

  if (person.persona === 'superadmin') {
    return (
      <Card title="Assigned work">
        <p className="team__quiet">
          A platform administrator already reaches every course and library on the platform.
          Assigning individual ones would not change anything for them.
        </p>
      </Card>
    );
  }

  if (!canManage) {
    return (
      <Card title="Assigned work">
        <p className="team__quiet">
          Only an administrator of this academy can see and change which courses and
          libraries somebody has been given.
        </p>
      </Card>
    );
  }

  const data = assignments.data;
  const items = [...(data?.courses ?? []), ...(data?.libraries ?? [])];
  const wideOpen = data?.hasOrgRole ?? false;

  return (
    <Card
      title="Assigned work"
      action={
        // Not offered to somebody who already sees the whole academy: see the note above.
        data && !wideOpen ? (
          <Button
            variant="secondary"
            onClick={() => {
              setNotice(null);
              give.reset();
              setAdding((open) => !open);
            }}
          >
            {adding ? 'Cancel' : 'Give access to something'}
          </Button>
        ) : undefined
      }
    >
      {assignments.isLoading && <Spinner label="Loading what they have been given…" />}

      {assignments.isError && (
        <ActionError
          error={assignments.error}
          what="Their assignments could not be loaded."
        />
      )}

      {notice && <Banner tone="success">{notice}</Banner>}
      {give.isError && !adding && (
        <ActionError error={give.error} what="They were not given access." />
      )}
      {take.isError && !removingItem && (
        <ActionError error={take.error} what="Their access was not removed." />
      )}

      {data && wideOpen && (
        <Banner tone="info">
          <strong>{person.name} already sees everything in this academy.</strong> Their
          access level covers every course and every library here, so giving them
          individual ones would not change what they can reach. Lower their access level
          above first if you want them to see only certain things.
        </Banner>
      )}

      {data && !wideOpen && items.length === 0 && !adding && (
        <p className="team__quiet">
          Nothing yet — so they see none of this academy&rsquo;s courses or libraries. Give
          them one and it becomes the only one they can reach.
        </p>
      )}

      {data && adding && (
        <AssignForm
          slug={slug}
          orgs={orgs}
          held={items}
          busy={give.isPending}
          error={give.isError ? give.error : null}
          onSubmit={(id, level) => {
            setNotice(null);
            give.mutate({ id, level });
          }}
        />
      )}

      {items.length > 0 && (
        <>
          {wideOpen && (
            <p className="team__quiet">
              They hold these individually as well. Right now these add nothing, because
              their academy-wide access already covers them.
            </p>
          )}
          <ul className="people__assignments">
            {items.map((item) => (
              <li key={item.id} className="people__assignment">
                <div className="people__assignment-what">
                  <Link to={assignmentHref(item)}>{item.title}</Link>
                  <span className="people__assignment-kind">
                    {item.kind === 'course' ? 'Course' : 'Library'}
                  </span>
                  <span className="mono">{item.id}</span>
                </div>
                <p className="team__quiet">{levelBlurb(item.level)}</p>
                <div className="people__assignment-controls">
                  <label className="team__persona">
                    <span className="team__persona-label">
                      What {person.name} can do in {item.title}
                    </span>
                    <select
                      className="admin__select"
                      value={item.level}
                      aria-label={`What ${person.name} can do in ${item.title}`}
                      disabled={give.isPending}
                      onChange={(event) => {
                        const level = event.target.value as AssignmentLevel;
                        if (level === item.level) return;
                        setNotice(null);
                        give.mutate({ id: item.id, level });
                      }}
                    >
                      {levelOptions(item.kind).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setNotice(null);
                      take.reset();
                      setRemovingItem(item);
                    }}
                  >
                    Remove access
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {removingItem && (
        <Dialog
          title="Remove their access?"
          onClose={() => setRemovingItem(null)}
          footer={
            <>
              <Button
                variant="danger"
                busy={take.isPending}
                onClick={() => take.mutate(removingItem)}
              >
                Remove access
              </Button>
              <Button variant="ghost" onClick={() => setRemovingItem(null)}>
                Cancel
              </Button>
            </>
          }
        >
          <p>
            <strong>{person.name}</strong> will no longer be able to open{' '}
            <strong>{removingItem.title}</strong>, and it will stop appearing in their lists.
          </p>
          {/* Said outright, because this is the fear that stops an administrator using the
              button at all — and the wording has to survive being skim-read. */}
          <p className="team__quiet">
            The {removingItem.kind === 'course' ? 'course' : 'library'} itself is not
            deleted, nothing they made in it is removed, and their account is untouched. You
            can give them access again at any time.
          </p>
          {take.isError && (
            <ActionError error={take.error} what="Their access was not removed." />
          )}
        </Dialog>
      )}
    </Card>
  );
}

/**
 * Choosing something to give, and how much of it.
 *
 * The list is THIS ACADEMY'S courses and libraries and nothing else — courses are asked for
 * one organisation at a time and libraries come from the tenant-scoped listing, so nothing
 * from another academy can appear in it. The server checks the same thing again on the way
 * in; this only keeps the administrator from being offered a choice that would be refused.
 */
function AssignForm({
  slug,
  orgs,
  held,
  busy,
  error,
  onSubmit,
}: {
  slug: string;
  orgs: string[];
  held: Assignment[];
  busy: boolean;
  error: unknown;
  onSubmit: (id: string, level: AssignmentLevel) => void;
}) {
  const [chosen, setChosen] = useState('');
  const [level, setLevel] = useState<AssignmentLevel>('staff');

  const catalogue = useQuery({
    queryKey: ['assignable', slug, orgs.join(',')],
    queryFn: () => loadAssignable(slug, orgs),
    staleTime: 5 * 60_000,
  });

  const alreadyHeld = new Set(held.map((item) => item.id));
  const all = catalogue.data ?? [];
  const courses = all.filter((item) => item.kind === 'course' && !alreadyHeld.has(item.id));
  const libraries = all.filter((item) => item.kind === 'library' && !alreadyHeld.has(item.id));
  const picked = all.find((item) => item.id === chosen);
  // The two vocabularies do not overlap, so the level has to follow the kind rather than
  // persist across a change of mind — 'staff' means nothing to a library.
  const kind = picked?.kind ?? 'course';
  const options = levelOptions(kind);
  const valid = options.some(([value]) => value === level);

  if (catalogue.isLoading) return <Spinner label="Loading this academy's courses and libraries…" />;

  if (catalogue.isError) {
    return (
      <Banner tone="danger">
        <strong>This academy&rsquo;s courses and libraries could not be loaded.</strong> Retry
        in a moment.
      </Banner>
    );
  }

  if (courses.length === 0 && libraries.length === 0) {
    return (
      <p className="team__quiet">
        There is nothing left to give them — they already have everything this academy has.
      </p>
    );
  }

  return (
    <form
      className="people__assign"
      onSubmit={(event) => {
        event.preventDefault();
        if (!picked) return;
        onSubmit(picked.id, valid ? level : (options[0]?.[0] as AssignmentLevel));
      }}
    >
      <div className="ui-field">
        <label htmlFor="assign-what">Course or library</label>
        <select
          id="assign-what"
          value={chosen}
          disabled={busy}
          onChange={(event) => {
            setChosen(event.target.value);
            const next = all.find((item) => item.id === event.target.value);
            setLevel(next?.kind === 'library' ? 'read' : 'staff');
          }}
        >
          <option value="">Choose one…</option>
          {courses.length > 0 && (
            <optgroup label="Courses">
              {courses.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.title}
                </option>
              ))}
            </optgroup>
          )}
          {libraries.length > 0 && (
            <optgroup label="Libraries">
              {libraries.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.title}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>

      {picked && (
        <>
          <SelectField
            label={`What they will be able to do in ${picked.title}`}
            id="assign-level"
            value={valid ? level : options[0]?.[0]}
            disabled={busy}
            options={options}
            onChange={(event) => setLevel(event.target.value as AssignmentLevel)}
          />
          <p className="team__quiet">{levelBlurb(valid ? level : (options[0]?.[0] ?? ''))}</p>
        </>
      )}

      {error != null && <ActionError error={error} what="They were not given access." />}

      <Button type="submit" variant="primary" busy={busy} disabled={!picked}>
        Give access
      </Button>
    </form>
  );
}

/**
 * This academy's courses and libraries, as things that can be assigned.
 *
 * Courses are asked for ONE ORGANISATION AT A TIME rather than unfiltered: Studio's listing
 * answers with everything the caller can reach, which for a platform operator is every
 * academy on the install. Naming the org is what keeps another academy's courses out of
 * this picker. Libraries already come from our tenant-scoped listing, which does the same
 * thing server-side.
 */
async function loadAssignable(slug: string, orgs: string[]): Promise<Assignment[]> {
  const [courses, libraries] = await Promise.all([
    Promise.all(
      orgs.map((org) => listCourses({ org, archived: false, pageSize: ASSIGNABLE_LIMIT })),
    ),
    listLibraries(slug),
  ]);

  const found: Assignment[] = [];
  for (const page of courses) {
    for (const course of page.courses) {
      found.push({
        kind: 'course',
        id: course.course_key,
        title: course.display_name || course.course_key,
        level: 'staff',
      });
    }
  }
  for (const library of libraries) {
    found.push({
      kind: 'library',
      id: library.id,
      title: library.title || library.id,
      level: 'read',
    });
  }
  return found.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
}

/**
 * One page, deliberately: a picker is a menu, not a catalogue. An academy with more courses
 * than this needs a search box rather than a longer <select>, and that is a different
 * control than the one this card should grow first.
 */
const ASSIGNABLE_LIMIT = 100;

/**
 * Takes the "view answers" handler rather than being a constant: opening the drill-in
 * needs the page's own state, and a `Column` list built once at module load has no way to
 * reach it.
 */
function enrolmentColumns(onViewAnswers: (course: MemberEnrolment) => void): Column<MemberEnrolment>[] {
  return [
    {
      key: 'course',
      header: 'Course',
      render: (course) => (
        <>
          <Link to={courseHref(course.courseId)}>{course.title}</Link>
          <div className="admin__sub mono">{course.courseId}</div>
        </>
      ),
    },
    {
      key: 'answers',
      header: '',
      render: (course) => (
        <Button variant="ghost" onClick={() => onViewAnswers(course)}>
          View answers
        </Button>
      ),
    },
  ];
}

/**
 * A course's title when this person's own enrolments name it, its key otherwise.
 *
 * Teaching a course and taking it are different lists, and only the second carries
 * titles — the API sends the key for a course team place. Showing the key rather than
 * inventing a lookup keeps the page honest: it is the identifier the admin will meet in
 * URLs and support tickets anyway.
 */
function titleOf(courseId: string, person: Member): string {
  return person.enrolments.find((course) => course.courseId === courseId)?.title ?? courseId;
}

/** Back to the list. `/admin/team` and `/admin/learners` are the same module. */
function BackLink() {
  return (
    <p className="people__back">
      <Link to="/admin/team">← All people</Link>
    </p>
  );
}
